import React, { useState, useEffect } from 'react';
import '../styles/signals-board.css';

const SignalsBoard = () => {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMarket, setSelectedMarket] = useState('US');
  const [selectedRegime, setSelectedRegime] = useState(null);
  const [limit, setLimit] = useState(50);
  const [modal, setModal] = useState({ open: false, title: '', body: null });

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

  const SIGNAL_EXPLAINS = {
    ema_pullback: {
      title: 'EMA Pullback (20/50)',
      body:
        'Look for price above EMA20 and EMA20 above EMA50. Enter on pullback toward EMA20 with strength returning (bounce candle). Stop below swing low; first target recent high.',
    },
    breakout: {
      title: 'Range Breakout',
      body:
        'Price breaking above recent range high on rising volume. Enter on breakout close or retest. Stop just below breakout level; target measured move of range height.',
    },
    range_breakout: {
      title: 'Range Breakout',
      body:
        'Price breaking above recent range high on rising volume. Enter on breakout close or retest. Stop just below breakout level; target measured move of range height.',
    },
    volume_spike: {
      title: 'Volume Spike',
      body:
        'Current volume > 2× 20-day average, confirming participation. Combine with trend filter; avoid chasing extended candles.',
    },
    rsi_crossing_50: {
      title: 'RSI Cross 50',
      body:
        'RSI crossing above 50 signals momentum shift to bull bias. Enter when aligned with trend and volume confirmation.',
    },
  };

  const sanitizeTitle = (title) => {
    if (!title) return null;
    const t = String(title).replace(/\s+/g, ' ').trim();
    if (!t) return null;
    // Drop obviously raw JSON-ish blobs
    if ((t.includes('{') && t.includes('}')) || (t.includes('[') && t.includes(']'))) return null;
    if (t.length > 180) return `${t.slice(0, 177)}…`;
    return t;
  };

  const sanitizeSource = (source) => {
    if (!source) return null;
    const t = String(source).trim();
    if (!t) return null;
    if ((t.includes('{') && t.includes('}')) || (t.includes('[') && t.includes(']'))) return null;
    return t;
  };

  const fallbackNews = (symbol) => {
    const query = symbol ? `${symbol} stock news` : 'stock news';
    return {
      title: `View news for ${symbol || 'this symbol'}`,
      url: `https://news.google.com/search?q=${encodeURIComponent(query)}`,
    };
  };

  const getField = (obj, fields) => {
    if (!obj) return null;
    for (const f of fields) {
      const parts = f.split('.');
      let ref = obj;
      for (const p of parts) {
        if (ref && typeof ref === 'object' && p in ref) {
          ref = ref[p];
        } else {
          ref = null;
          break;
        }
      }
      if (ref !== null && ref !== undefined) return ref;
    }
    return null;
  };

  // Normalize any shape of `news` (array, JSON string, Python-ish string)
  const normalizeNews = (raw, symbol) => {
    const items = [];
    if (!raw) return items;

    const coerceArray = (val) => (Array.isArray(val) ? val : [val]);

    const pythonishToJson = (s) => {
      if (typeof s !== 'string') return s;
      let t = s.trim();
      // Wrap standalone objects with [] to split consistently later
      if (t.startsWith('{') && t.endsWith('}')) t = `[${t}]`;
      // Replace Python literals and quotes
      t = t
        .replace(/\bNone\b/g, 'null')
        .replace(/\bTrue\b/g, 'true')
        .replace(/\bFalse\b/g, 'false')
        // Ensure object keys are quoted if missing quotes
        .replace(/([\{,]\s*)([A-Za-z_][A-Za-z0-9_]*)\s*:/g, '$1"$2":')
        // Convert single-quoted strings to double quotes (basic heuristic)
        .replace(/'([^']*)'/g, '"$1"');
      return t;
    };

    const parseMaybe = (val) => {
      if (typeof val === 'object') return val;
      if (typeof val !== 'string') return null;
      // First try JSON as-is
      try {
        return JSON.parse(val);
      } catch (_) {
        // Try python-ish to JSON coercion
        try {
          const fixed = pythonishToJson(val);
          return JSON.parse(fixed);
        } catch (_) {
          return null;
        }
      }
    };

    const parseContent = (val) => {
      if (!val) return null;
      const parsed = parseMaybe(val);
      if (parsed && typeof parsed === 'object') return parsed;
      return val && typeof val === 'object' ? val : null;
    };

    const splitObjects = (str) => {
      const s = String(str).trim();
      if (!s) return [];
      // If it already looks like JSON array, return as one piece
      if ((s.startsWith('[') && s.endsWith(']')) || (s.startsWith('{') && s.endsWith('}'))) return [s];
      // Fallback: split between closing and opening braces where separated by commas/newlines
      return s.split(/}\s*,?\s*(?=\{)/g).map((chunk, idx, arr) => (idx < arr.length - 1 ? chunk + '}' : chunk));
    };

    const extractDisplay = (objOrStr) => {
      const obj = parseMaybe(objOrStr) || (typeof objOrStr === 'object' ? objOrStr : null);
      if (obj) {
        const contentObj = parseContent(obj.content) || obj.content || obj;
        const title =
          getField(contentObj, ['title', 'headline', 'name', 'summary']) ||
          getField(obj, ['title', 'headline', 'name', 'summary']);
        const url =
          getField(contentObj, ['clickThroughUrl.url', 'canonicalUrl.url', 'url', 'link']) ||
          getField(obj, ['url', 'link']);
        const source =
          getField(contentObj, ['provider.displayName', 'source', 'publisher']) ||
          getField(obj, ['provider.displayName', 'source', 'publisher']);
        return { title, url, source };
      }

      // Last-resort regex extraction from python-ish string
      const s = String(objOrStr);
      if (s) {
        const titleMatch = s.match(/title\s*[:=]\s*['"]([^'"]+)['"]/i);
        const urlMatch =
          s.match(/clickThroughUrl\s*:\s*\{[^}]*url\s*[:=]\s*['"]([^'"]+)['"]/i) ||
          s.match(/canonicalUrl\s*:\s*\{[^}]*url\s*[:=]\s*['"]([^'"]+)['"]/i) ||
          s.match(/\burl\s*[:=]\s*['"]([^'"]+)['"]/i) ||
          s.match(/\blink\s*[:=]\s*['"]([^'"]+)['"]/i);
        const sourceMatch = s.match(/provider\s*:\s*\{[^}]*displayName\s*[:=]\s*['"]([^'"]+)['"]/i) ||
          s.match(/\bsource\s*[:=]\s*['"]([^'"]+)['"]/i) ||
          s.match(/\bpublisher\s*[:=]\s*['"]([^'"]+)['"]/i);
        const fallbackTitle = titleMatch ? titleMatch[1] : s;
        return {
          title: fallbackTitle,
          url: urlMatch ? urlMatch[1] : undefined,
          source: sourceMatch ? sourceMatch[1] : undefined,
        };
      }
      return null;
    };

    // Normalize input into iterable pieces
    const pieces = Array.isArray(raw)
      ? raw
      : splitObjects(raw)
          .map((piece) => piece && piece.trim())
          .filter(Boolean);

    coerceArray(pieces).forEach((x) => {
      const info = extractDisplay(x);
      const cleanTitle = sanitizeTitle(info?.title);
      const cleanSource = sanitizeSource(info?.source);

      // Prefer provided URL, else build from title, else fallback to symbol search
      const cleanUrl =
        info?.url && typeof info.url === 'string'
          ? info.url
          : cleanTitle
            ? `https://news.google.com/search?q=${encodeURIComponent(cleanTitle)}`
            : null;

      if (cleanTitle || cleanUrl) {
        items.push({
          title: cleanTitle || `News for ${symbol || 'this symbol'}`,
          url: cleanUrl || fallbackNews(symbol).url,
          source: cleanSource || undefined,
        });
      }
    });

    if (items.length === 0) items.push(fallbackNews(symbol));

    return items;
  };

  useEffect(() => {
    fetchSignals();
  }, [selectedMarket, selectedRegime, limit]);

  const fetchSignals = async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API_URL}/api/signals/swing?market=${selectedMarket}&limit=${limit}`;
      if (selectedRegime) {
        url += `&regime=${selectedRegime}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setSignals(data.signals || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching signals:', err);
    } finally {
      setLoading(false);
    }
  };

  const getSignalBadges = (signals) => {
    if (!Array.isArray(signals) || signals.length === 0) return [];
    return signals.slice(0, 3).map((signal, idx) => (
      <button
        key={idx}
        className="signal-badge"
        onClick={() => handleSignalExplain(signal)}
        type="button"
      >
        {signal.replace(/_/g, ' ')}
      </button>
    ));
  };

  const getRegimeColor = (regime) => {
    switch (regime?.toLowerCase()) {
      case 'bull':
        return '#22c55e'; // green
      case 'bear':
        return '#ef4444'; // red
      case 'consolidation':
        return '#f59e0b'; // amber
      default:
        return '#6b7280'; // gray
    }
  };

  const formatDecision = (decision) => {
    if (!decision) return null;
    const cleaned = decision.replace(/_/g, ' ');
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  };

  const getInsight = (signal) => {
    const notes = signal?.llm_review?.notes || signal?.full?.llm_review?.notes;
    if (Array.isArray(notes) && notes.length > 0) return notes[0];
    return null;
  };

  const handleSignalExplain = (signalKey) => {
    const key = (signalKey || '').toLowerCase();
    const info = SIGNAL_EXPLAINS[key] || {
      title: signalKey,
      body: 'Trade the signal with proper risk management and confirm with volume/trend filters.',
    };
    setModal({ open: true, title: info.title, body: info.body });
  };

  const handleReviewExplain = (signal) => {
    const review = signal?.llm_review || {};
    const notes = review.notes || signal?.full?.llm_review?.notes || [];
    const risk = review.risk_factors || [];
    const raw = review._llm_raw || signal?.full?._llm_raw;
    const lines = [];
    lines.push(`Decision: ${formatDecision(review.decision) || 'N/A'}`);
    if (review.confidence) lines.push(`Confidence: ${(review.confidence * 100).toFixed(0)}%`);
    if (notes.length) lines.push(`Notes: ${notes.join(' | ')}`);
    if (risk.length) lines.push(`Risks: ${risk.join(' | ')}`);
    if (raw) lines.push(`LLM Input: ${raw}`);
    setModal({ open: true, title: 'LLM Review Details', body: lines.join('\n') || 'No additional details.' });
  };

  const closeModal = () => setModal({ open: false, title: '', body: null });

  return (
    <div className="signals-board">
      <div className="signals-header">
        <h2>🎯 Swing Trading Signals</h2>
        <div className="signals-controls">
          <select
            value={selectedMarket}
            onChange={(e) => setSelectedMarket(e.target.value)}
            className="market-select"
          >
            <option value="US">🇺🇸 US Market</option>
            <option value="CA">🇨🇦 Canada</option>
            <option value="VN">🇻🇳 Vietnam</option>
          </select>

          <select
            value={selectedRegime || ''}
            onChange={(e) => setSelectedRegime(e.target.value || null)}
            className="regime-select"
          >
            <option value="">All Regimes</option>
            <option value="bull">📈 Bull</option>
            <option value="bear">📉 Bear</option>
            <option value="consolidation">➡️ Consolidation</option>
          </select>

          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value))}
            className="limit-select"
          >
            <option value={10}>10 Signals</option>
            <option value={25}>25 Signals</option>
            <option value={50}>50 Signals</option>
            <option value={100}>100 Signals</option>
          </select>

          <button onClick={fetchSignals} className="refresh-btn">
            🔄 Refresh
          </button>
        </div>
      </div>

      {loading && <div className="loading-state">⏳ Loading signals...</div>}

      {error && <div className="error-state">❌ Error: {error}</div>}

      {!loading && signals.length === 0 && (
        <div className="empty-state">📊 No signals found for selected criteria</div>
      )}

      {!loading && signals.length > 0 && (
        <div className="signals-container">
          <div className="signals-info">
            Found <strong>{signals.length}</strong> swing trading signals
          </div>

          <div className="signals-grid">
            {signals.map((signal, idx) => (
              <div key={idx} className="signal-card">
                <div className="signal-header">
                  <div className="symbol-section">
                    <h3 className="symbol">{signal.symbol}</h3>
                    <span
                      className="regime-badge"
                      style={{ backgroundColor: getRegimeColor(signal.regime) }}
                    >
                      {signal.regime?.toUpperCase()}
                    </span>
                  </div>
                  <div className="timestamp">
                    {signal.created_at ? new Date(signal.created_at).toLocaleDateString() : 'N/A'}
                  </div>
                </div>

                <div className="signal-body">
                  {signal.signals && signal.signals.length > 0 && (
                    <div className="signals-section">
                      <label>Signals</label>
                      <div className="badges-container">{getSignalBadges(signal.signals)}</div>
                    </div>
                  )}

                  {signal.news_count > 0 && (
                    <div className="news-section">
                      <label>📰 News</label>
                      {(() => {
                        const normalized = normalizeNews(signal.news, signal.symbol);
                        return normalized && normalized.length > 0 ? (
                        <ul className="news-list">
                          {normalized.slice(0, 3).map((item, i) => (
                            <li key={i}>
                              {item.url ? (
                                <a href={item.url} target="_blank" rel="noreferrer">
                                  {item.title}
                                </a>
                              ) : (
                                <span>{item.title}</span>
                              )}
                              {item.source && <span className="news-source"> · {item.source}</span>}
                            </li>
                          ))}
                        </ul>
                        ) : (
                          <span className="news-count">{signal.news_count} articles</span>
                        );
                      })()}
                    </div>
                  )}

                  {signal.llm_review && signal.llm_review.decision && (
                    <div className="review-section">
                      <label>LLM Review</label>
                      <button className="review-decision" type="button" onClick={() => handleReviewExplain(signal)}>
                        {signal.llm_review.decision.replace(/_/g, ' ')}
                        {signal.llm_review.confidence && (
                          <span className="confidence">
                            {(signal.llm_review.confidence * 100).toFixed(0)}%
                          </span>
                        )}
                      </button>
                    </div>
                  )}
                </div>

                <div className="signal-footer">
                  <a href={`/stock/${signal.symbol}`} className="view-detail-btn">
                    View Details →
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {modal.open && (
        <div className="modal-backdrop" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>{modal.title}</h4>
              <button className="modal-close" onClick={closeModal} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-body">
              {modal.body?.split('\n').map((line, idx) => (
                <p key={idx}>{line}</p>
              ))}
            </div>
            <div className="modal-footer">
              <button className="refresh-btn" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignalsBoard;
